import axios from "axios";

const api = axios.create({
  // baseURL: "http://localhost:8009/murasalone/v2/api",
  baseURL: "https://spacehubbackend.insyncproducts.online/murasalone/v2/api",
  // baseURL: "http://109.205.180.193:8011/murasalone/v2/api",
  headers: {
    "Content-Type": "application/json",
  },
});

// ==============================
// REQUEST INTERCEPTOR
// ==============================
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// ==============================
// RESPONSE INTERCEPTOR
// ==============================
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  },
);

// ======================================================
// AUTH APIs
// ======================================================

export const loginApi = (data) => api.post("/auth/login", data);
export const refreshTokenApi = (data) => api.post("/auth/refresh", data);
export const logoutApi = (data) => api.post("/auth/logout", data);
export const setPasswordApi = (data) => api.post("/auth/set-password", data);

// ======================================================
// USERS APIs
// ======================================================

export const getUsersApi = (page = 1, limit = 100, search = "") =>
  api.get(`/users?page=${page}&limit=${limit}&search=${search}`);

export const getUserByIdApi = (id) => api.get(`/users/${id}`);
export const createUserApi = (data) => api.post("/users", data);
export const updateUserApi = (id, data) => api.patch(`/users/${id}`, data);
export const updateUserStatusApi = (id, data) =>
  api.patch(`/users/${id}/status`, data);
export const resetUserPasswordApi = (id) =>
  api.post(`/users/${id}/reset-password`);
export const resetUserSessionsApi = (id) =>
  api.post(`/users/${id}/reset-sessions`);

// =================================================
// GROUPS APIs
// ======================================================

export const getGroupsApi = (page = 1, limit = 20) =>
  api.get(`/groups?page=${page}&limit=${limit}`);
export const getGroupByIdApi = (id) => api.get(`/groups/${id}`);
export const createGroupApi = (data) => api.post("/groups", data);
export const addGroupMemberApi = (groupId, data) =>
  api.post(`/groups/${groupId}/members`, data);
export const removeGroupMemberApi = (groupId, userId) =>
  api.delete(`/groups/${groupId}/members/${userId}`);
export const updateGroupStatusApi = (groupId, data) =>
  api.patch(`/groups/${groupId}/status`, data);
export const updateGroupApi = (groupId, data) =>
  api.patch(`/groups/${groupId}`, data);
export const deleteGroupApi = (groupId) => api.delete(`/groups/${groupId}`);

// ======================================================
// ROLES APIs
// ======================================================

export const getRolesApi = () => api.get("/roles");
export const createRoleApi = (data) => api.post("/roles", data);
export const updateRoleApi = (id, data) => api.patch(`/roles/${id}`, data);
export const deleteRoleApi = (id) => api.delete(`/roles/${id}`);

// ======================================================
// DEPARTMENTS APIs
// ======================================================

export const getDepartmentsApi = () => api.get("/departments");
export const createDepartmentApi = (data) => api.post("/departments", data);

// ======================================================
// DASHBOARD APIs
// ======================================================

export const getDashboardSummaryApi = () => api.get("/dashboard/summary");

// ======================================================
// MINIO DIRECT UPLOAD APIs
// ======================================================

export const registerMediaApi = (data) => api.post("/media/register", data);
export const updateProfileImageApi = (data) =>
  api.patch("/profile/image", data);
export const getPresignedUploadUrlApi = (data) =>
  api.post("/media/presigned-upload", data);

// ======================================================
// PROFILE APIs
// ======================================================

export const getProfileApi = () => api.get("/profile");
export const updateProfileApi = (data) => api.patch("/profile", data);
export const updatePasswordApi = (data) => api.patch("/profile/password", data);

// ======================================================
// PROFILE SESSION APIs
// ======================================================

export const getProfileSessionsApi = () => api.get("/profile/sessions");
export const revokeProfileSessionApi = (sessionId) =>
  api.delete(`/profile/sessions/${sessionId}`);
export const revokeAllProfileSessionsApi = () =>
  api.delete("/profile/sessions");

// ─────────────────────────────────────────────
// REPORT APIs
// ─────────────────────────────────────────────

export const getUserActivityReportApi = (
  page = 1,
  limit = 20,
  department = "",
  role = "",
  status = "",
  fromDate = "",
  toDate = "",
) =>
  api.get(
    `/reports/user-activity?page=${page}&limit=${limit}&department=${department}&role=${role}&status=${status}&fromDate=${fromDate}&toDate=${toDate}`,
  );

export const getRoomActivityReportApi = (
  page = 1,
  limit = 20,
  department = "",
  roomType = "",
  status = "",
  fromDate = "",
  toDate = "",
) =>
  api.get(
    `/reports/room-activity?page=${page}&limit=${limit}&department=${department}&roomType=${roomType}&status=${status}&fromDate=${fromDate}&toDate=${toDate}`,
  );

export const getWhatsappRoutingReportApi = (
  page = 1,
  limit = 20,
  status = "",
  type = "",
  phone = "",
  room = "",
  fromDate = "",
  toDate = "",
) =>
  api.get(
    `/reports/whatsapp-routing?page=${page}&limit=${limit}&status=${status}&type=${type}&phone=${phone}&room=${room}&fromDate=${fromDate}&toDate=${toDate}`,
  );

export const getMediaStorageReportApi = (
  page = 1,
  limit = 20,
  fileType = "",
  status = "",
  room = "",
  fromDate = "",
  toDate = "",
) =>
  api.get(
    `/reports/media-storage?page=${page}&limit=${limit}&fileType=${fileType}&status=${status}&room=${room}&fromDate=${fromDate}&toDate=${toDate}`,
  );

export const getSecurityEventsReportApi = (
  page = 1,
  limit = 20,
  action = "",
  user = "",
  fromDate = "",
  toDate = "",
) =>
  api.get(
    `/reports/security-events?page=${page}&limit=${limit}&action=${action}&user=${user}&fromDate=${fromDate}&toDate=${toDate}`,
  );

export const getBroadcastEngagementReportApi = (
  page = 1,
  limit = 20,
  audienceType = "",
  status = "",
  fromDate = "",
  toDate = "",
) =>
  api.get(
    `/reports/broadcast-engagement?page=${page}&limit=${limit}&audienceType=${audienceType}&status=${status}&fromDate=${fromDate}&toDate=${toDate}`,
  );

// ======================================================
// TEAMS APIs
// ======================================================

export const getTeamsApi = (
  page = 1,
  limit = 20,
  search = "",
  department = "",
  type = "",
  status = "",
) =>
  api.get(
    `/teams?page=${page}&limit=${limit}&search=${search}&department=${department}&type=${type}&status=${status}`,
  );

export const getTeamByIdApi = (id) => api.get(`/teams/${id}`);
export const createTeamApi = (data) => api.post("/teams", data);
export const updateTeamApi = (id, data) => api.patch(`/teams/${id}`, data);
export const deleteTeamApi = (id) => api.delete(`/teams/${id}`);
export const addTeamMemberApi = (teamId, data) =>
  api.post(`/teams/${teamId}/members`, data);
export const updateTeamMemberRoleApi = (teamId, userId, data) =>
  api.patch(`/teams/${teamId}/members/${userId}`, data);
export const removeTeamMemberApi = (teamId, userId) =>
  api.delete(`/teams/${teamId}/members/${userId}`);
export const createTeamChannelApi = (teamId, data) =>
  api.post(`/teams/${teamId}/channels`, data);
export const updateChannelApi = (channelId, data) =>
  api.patch(`/teams/channels/${channelId}`, data);
export const getTeamAuditLogsApi = (teamId, page = 1, limit = 20) =>
  api.get(`/teams/${teamId}/audit?page=${page}&limit=${limit}`);

// ======================================================
// CHANNEL MEMBERS APIs
// ======================================================

export const getChannelMembersApi = (channelId) =>
  api.get(`/teams/channels/${channelId}/members`);
export const addChannelMemberApi = (channelId, data) =>
  api.post(`/teams/channels/${channelId}/members`, data);
export const removeChannelMemberApi = (channelId, userId) =>
  api.delete(`/teams/channels/${channelId}/members/${userId}`);

// ======================================================
// AUDIT APIs
// ======================================================

export const getAuditLogsApi = (page = 1, limit = 20) =>
  api.get(`/audit?page=${page}&limit=${limit}`);
export const getAuditLogByIdApi = (id) => api.get(`/audit/${id}`);

// ======================================================
// BROADCAST APIs
// ======================================================

export const getBroadcastsApi = (
  page = 1,
  limit = 10,
  search = "",
  status = "",
  type = "",
  priority = "",
) =>
  api.get(
    `/broadcasts?page=${page}&limit=${limit}&search=${search}&status=${status}&type=${type}&priority=${priority}`,
  );

export const getBroadcastByIdApi = (id) => api.get(`/broadcasts/${id}`);
export const createBroadcastApi = (data) => api.post("/broadcasts", data);
export const updateBroadcastApi = (id, data) =>
  api.patch(`/broadcasts/${id}`, data);
export const updateBroadcastStatusApi = (id, data) =>
  api.patch(`/broadcasts/${id}/status`, data);
export const deleteBroadcastApi = (id) => api.delete(`/broadcasts/${id}`);
export const resendBroadcastApi = (id) => api.post(`/broadcasts/${id}/resend`);
export const getBroadcastTemplatesApi = () => api.get("/broadcasts/templates");

// ─────────────────────────────────────────────
// WHATSAPP MAPPING APIs
// ─────────────────────────────────────────────

export const getWhatsappMappingsApi = () => api.get("/whatsapp/mappings");
export const createWhatsappMappingApi = (data) =>
  api.post("/whatsapp/mappings", data);
export const updateWhatsappMappingApi = (id, data) =>
  api.patch(`/whatsapp/mappings/${id}`, data);
export const deleteWhatsappMappingApi = (id) =>
  api.delete(`/whatsapp/mappings/${id}`);

// ─────────────────────────────────────────────
// WHATSAPP GROUP APIs
// ─────────────────────────────────────────────

export const getWhatsappGroupsApi = () => api.get("/whatsapp/groups");

// ───────────────── MEDIA REPOSITORY ─────────────────

export const getMediaRepositoryApi = (
  page = 1,
  limit = 20,
  search = "",
  status = "",
  type = "",
  room = "",
) =>
  api.get(
    `/media?page=${page}&limit=${limit}&search=${search}&status=${status}&type=${type}&room=${room}`,
  );

export const getSingleMediaApi = (id) => api.get(`/media/${id}`);
export const quarantineMediaApi = (id) => api.patch(`/media/${id}/quarantine`);
export const restoreMediaApi = (id) => api.patch(`/media/${id}/restore`);
export const deleteMediaApi = (id) => api.delete(`/media/${id}`);

// ─────────────────────────────────────────────
// WHATSAPP USERS APIs
// ─────────────────────────────────────────────

export const getWhatsappUsersApi = () => api.get("/whatsapp/users");

// ======================================================
// SEARCH APIs
// ======================================================

export const searchUsersApi = (query) => api.get(`/users/search?q=${query}`);

// ======================================================
// DEVICE APIs
// ======================================================

export const registerDeviceTokenApi = (data) =>
  api.post("/devices/token", data);

// ======================================================
// CALL APIs
// ======================================================

export const startCallApi = (data) => api.post("/calls/start", data);
export const acceptCallApi = (callId) => api.post(`/calls/${callId}/accept`);
export const endCallApi = (callId) => api.post(`/calls/${callId}/end`);
export const joinCallApi = (callId) => api.post(`/calls/${callId}/join`);
export const startGroupCallApi = (groupId, data) =>
  api.post(`/calls/groups/${groupId}/start`, data);

// ─────────────────────────────────────────────
// SUPPORT TICKETS APIs
// ─────────────────────────────────────────────

export const getSupportTicketsApi = (
  page = 1,
  limit = 20,
  status = "",
  priority = "",
  search = "",
) =>
  api.get(
    `/support/tickets?page=${page}&limit=${limit}&status=${status}&priority=${priority}&search=${search}`,
  );

export const getSupportTicketByIdApi = (id) =>
  api.get(`/support/tickets/${id}`);
export const createSupportTicketApi = (data) =>
  api.post("/support/tickets", data);
export const resolveSupportTicketApi = (id) =>
  api.patch(`/support/tickets/${id}/resolve`);
export const addSupportTicketNoteApi = (id, data) =>
  api.post(`/support/tickets/${id}/notes`, data);

// ─────────────────────────────────────────────
// SUPPORT FAQ APIs
// ─────────────────────────────────────────────

export const getSupportFaqsApi = () => api.get("/support/faqs");
export const createSupportFaqApi = (data) => api.post("/support/faqs", data);
export const updateSupportFaqApi = (id, data) =>
  api.patch(`/support/faqs/${id}`, data);
export const deleteSupportFaqApi = (id) => api.delete(`/support/faqs/${id}`);

// ─────────────────────────────────────────────
// SUPPORT ESCALATION APIs
// ─────────────────────────────────────────────

export const getSupportEscalationsApi = () => api.get("/support/escalations");

// ======================================================
// HEALTH APIs
// ======================================================

export const getHealthApi = () => api.get("/health");

// ─────────────────────────────────────────────
// WHATSAPP ROUTING LOG APIs
// ─────────────────────────────────────────────

export const getWhatsappLogsApi = (
  page = 1,
  limit = 20,
  status = "",
  type = "",
  search = "",
) =>
  api.get(
    `/whatsapp/logs?page=${page}&limit=${limit}&status=${status}&type=${type}&search=${search}`,
  );

export const getWhatsappLogByIdApi = (id) => api.get(`/whatsapp/logs/${id}`);
export const retryWhatsappLogApi = (id) =>
  api.patch(`/whatsapp/logs/${id}/retry`);

// ======================================================
// SECURITY DASHBOARD APIs
// ======================================================

export const getSecurityDashboardApi = (range = "7d") =>
  api.get(`/security/dashboard?range=${range}`);
export const getSecurityAlertsApi = () => api.get("/security/alerts");
export const getSecurityRecentActivityApi = (limit = 20) =>
  api.get(`/security/recent-activity?limit=${limit}`);
export const sendSecurityAlertApi = (data) =>
  api.post("/security/send-alert", data);

// ======================================================
// MEETINGS APIs
// ======================================================

export const getMeetingsApi = (params = {}) =>
  api.get("/calendars/events", { params });

export const getMeetingJoinTokenApi = (meetingId) =>
  api.get(`/meetings/join-token/${meetingId}`);

export const createMursalMeetingApi = (data) =>
  api.post("/meetings/livekit", data);

export const createExchangeMeetingApi = (data) =>
  api.post("/meetings/exchange2019", data);

export const createExchangeMeetingAutoApi = (data) =>
  api.post("/meetings/exchange", data);

export const getAllMeetingsApi = (params = {}) =>
  api.get("/meetings", { params });

export const getMeetingByIdApi = (meetingId) =>
  api.get(`/meetings/${meetingId}`);

export const updateMeetingApi = (meetingId, data) =>
  api.patch(`/meetings/${meetingId}`, data);

export const cancelMeetingApi = (meetingId) =>
  api.delete(`/meetings/${meetingId}`);

export const addParticipantApi = (meetingId, data) =>
  api.post(`/meetings/${meetingId}/participants`, data);

export const removeParticipantApi = (meetingId, participantId) =>
  api.delete(`/meetings/${meetingId}/participants/${participantId}`);

export const getMeetingStatsApi = () => api.get("/meetings/stats");

// ======================================================
// MEETING RECORDING APIs
// ======================================================

export const startMeetingRecordingApi = (meetingId) =>
  api.post(`/meetings/recording/start/${meetingId}`);

export const stopRecordingApi = (recordingId) =>
  api.post(`/meetings/recordings/stop/${recordingId}`);

// ======================================================
// MEETING TRANSCRIPT APIs
// ======================================================

export const processTranscriptApi = (meetingId) =>
  api.post(`/meetings/transcript/process/${meetingId}`);

// ======================================================
// SCREEN SHARE APIs
// ======================================================

export const canShareScreenApi = (meetingId) =>
  api.get(`/meetings/${meetingId}/can-share-screen`);

export const startScreenShareApi = (meetingId, platform = "web") =>
  api.post(`/meetings/${meetingId}/screen-share/start`, { platform });

export const stopScreenShareApi = (meetingId, platform = "web") =>
  api.post(`/meetings/${meetingId}/screen-share/stop`, { platform });

export const hostStopScreenShareApi = (meetingId, targetUserId) =>
  api.post(`/meetings/${meetingId}/host/stop-screen-share`, { targetUserId });

export const getScreenShareStatusApi = (meetingId) =>
  api.get(`/meetings/${meetingId}/screen-share-status`);

export const getScreenShareLogsApi = (meetingId, params = {}) =>
  api.get(`/meetings/${meetingId}/screen-share-logs`, { params });

// ======================================================
// CALENDAR SYNC APIs
// ======================================================

export const syncGraphCalendarApi = (data) =>
  api.post("/calendar-sync/graph", data);

// ======================================================
// LIVEKIT WEBHOOK (server-to-server)
// ======================================================

export const livekitWebhookApi = (data) => api.post("/webhooks/livekit", data);

// ======================================================
// MEETING PARTICIPANT CONTROL APIs
// ======================================================

export const kickParticipantApi = (meetingId, participantId) =>
  api.post(`/meetings/${meetingId}/kick/${participantId}`);

export const muteParticipantApi = (meetingId, participantId) =>
  api.post(`/meetings/${meetingId}/mute/${participantId}`);

export const unmuteParticipantApi = (meetingId, participantId) =>
  api.post(`/meetings/${meetingId}/unmute/${participantId}`);

export const disableParticipantVideoApi = (meetingId, participantId) =>
  api.post(`/meetings/${meetingId}/disable-video/${participantId}`);

export const enableParticipantVideoApi = (meetingId, participantId) =>
  api.post(`/meetings/${meetingId}/enable-video/${participantId}`);

export const lockMeetingApi = (meetingId) =>
  api.post(`/meetings/${meetingId}/lock`);

export const unlockMeetingApi = (meetingId) =>
  api.post(`/meetings/${meetingId}/unlock`);

export const endMeetingForAllApi = (meetingId) =>
  api.post(`/meetings/${meetingId}/end`);

// Get full transcripts for a meeting
export const getMeetingTranscriptsApi = (meetingId) =>
  api.get(`/meetings/${meetingId}/transcripts`);

// Get full minutes for a meeting
export const getMeetingMinutesApi = (meetingId) =>
  api.get(`/meetings/${meetingId}/minutes`);
// ✅ ADD THESE TWO:
export const joinMeetingApi = (meetingId) =>
  api.post(`/meetings/join/${meetingId}`);

export const leaveMeetingApi = (meetingId) =>
  api.post(`/meetings/leave/${meetingId}`);

// ✅ ADD WAITING ROOM APIs HERE:
export const requestToJoinWaitingRoomApi = (meetingId) =>
  api.post(`/meetings/${meetingId}/waiting-room/request`);

export const checkWaitingRoomStatusApi = (meetingId) =>
  api.get(`/meetings/${meetingId}/waiting-room/check`);

export const getPendingWaitingRoomRequestsApi = (meetingId) =>
  api.get(`/meetings/${meetingId}/waiting-room/pending`);

export const approveWaitingRoomParticipantApi = (meetingId, userId) =>
  api.post(`/meetings/${meetingId}/waiting-room/approve/${userId}`);

export const rejectWaitingRoomParticipantApi = (meetingId, userId) =>
  api.post(`/meetings/${meetingId}/waiting-room/reject/${userId}`);

export const getAttendeeMeetingsApi = (params = {}) =>
  api.get("/meetings/attendee", { params });

export default api;
